// @ts-nocheck --> Important to remove ts-nocheck
/* eslint-disable no-shadow */
import React, { useCallback, useState, useMemo, useRef, useEffect, useContext } from 'react'
import { string, object, bool, func, array } from 'prop-types'
import DayPickerInput from 'react-day-picker/DayPickerInput'
import { DateUtils } from 'react-day-picker'
import styled from 'styled-components'
import classNames from 'classnames'
import { Container } from '@mch-group/uikit-components'
import { LoginContext } from '@providers/Login'
import tokens from '@mch-group/design-tokens/build/js/design_tokens-module'

import { mq, px2rem } from '@utils'

import {
  formatEventDate,
  buildEventsFilter,
  getDateRangeDisplayInputValue,
  formatDateDayPicker,
  parseDateDayPicker
} from '../../../utils/events'
import Text from '../../Text'
import BrowseLocations from './BrowseLocations'
import { Dropdown, Icon, Row, Col } from '@mch-group/uikit-components'

const mID = 'otm-browse-events'

const selectItem = {
  key: 'code',
  value: 'code',
  text: 'value',
  id: 'code',
  name: 'value',
  disabled: 'count'
}

const calendarDateFormat = 'ddd DD MMM YYYY'
const modifiersStyles = {
  eventDay: {
    color: tokens.color.base.primary.black.value
  }
}

const OtmBrowseFilters = ({
  className,
  isLoggedIn,
  eventsLabels,
  eventsFilters,
  eventsFiltersData,
  locationLabel,
  typeLabel,
  dayLabel,
  categoryLabel,
  sortByLabel,
  allLabel,
  allEventsLabel,
  allExhibitionsLabel,
  allVirtualEventsLabel,
  endDateLabel,
  startDateLabel,
  recentlyAddedLabel,
  moreLocationsLabel,
  selectedNumberLabel,
  availableFilters,
  onFilterChange,
  locationVisibility
}) => {
  const [isMobileFiltersVisible, setIsMobileFiltersVisible] = useState(false)
  const [datePickerRange, setDatePickerRange] = useState({ from: undefined, to: undefined })
  const signInCallbackFilterValue = useRef(null)
  const toDateRef = useRef()
  const locationsFilterVisible = locationVisibility === 'true'
  const { showLogin } = useContext(LoginContext)
  const defaultTypeOptions = useMemo(
    () =>
      [
        { code: 'events', value: allEventsLabel, count: 1 },
        { code: 'exhibitions', value: allExhibitionsLabel, count: 1 },
        { code: 'virtual', value: allVirtualEventsLabel, count: 1 }
      ].filter((item) => item.value && item.value !== '---'),
    [allEventsLabel, allExhibitionsLabel, allVirtualEventsLabel]
  )
  const typeFilterSelectedValue = useMemo(() => {
    if (eventsFilters.exhibition) {
      return eventsFilters.exhibition === 'true' ? 'exhibitions' : 'events'
    } else if (eventsFilters.access) {
      return eventsFilters.access
    } else if (eventsFilters.virtual) {
      return 'virtual'
    } else {
      return 'all'
    }
  }, [eventsFilters.exhibition, eventsFilters.access, eventsFilters.virtual])

  const defaultOrderByOptions = useMemo(
    () => [
      { code: 'endDate ASC', value: endDateLabel, count: 1 },
      { code: 'startDate ASC', value: startDateLabel, count: 1 },
      { code: 'dateCreated DESC', value: recentlyAddedLabel, count: 1 }
    ],
    [endDateLabel, startDateLabel, recentlyAddedLabel]
  )
  const eventDay = useMemo(
    () => (eventsFiltersData.AVAILABLE_DAYS ? eventsFiltersData.AVAILABLE_DAYS.map((item) => new Date(item.code)) : []),
    [eventsFiltersData.AVAILABLE_DAYS]
  )

  const isDayDisabled = useCallback(
    (day) =>
      !eventDay.some(
        (eventDay) =>
          (DateUtils.isSameDay(day, new Date()) && DateUtils.isSameDay(new Date(), eventDay)) ||
          DateUtils.isSameDay(day, eventDay)
      ),
    [eventDay]
  )

  const toggleMobileFilters = useCallback(() => {
    setIsMobileFiltersVisible((visible) => !visible)
  }, [])

  const onTypeFilterChange = useCallback(
    (_, data) => {
      let params

      switch (data.value) {
        case 'events':
        case 'exhibitions':
          params = {
            exhibition: data.value === 'events' ? 'false' : 'true',
            access: undefined,
            virtual: undefined
          }
          break
        case 'virtual':
          params = { virtual: 'true', access: undefined, exhibition: undefined }
          break
        case 'VIP':
        case 'PRIVATE_INVITATION':
          if (!isLoggedIn) {
            signInCallbackFilterValue.current = data.value
            // @ts-ignore
            setIsSignInActive(true)
            if (!isLoggedIn) {
              showLogin({
                onLoginSuccessCallback: () => {
                  onTypeFilterChange(null, { value: signInCallbackFilterValue.current })
                },
                closeLoginModalOnRouteChange: true
              })
            }
            return
          }
          params = { exhibition: undefined, access: data.value }
          break
        default:
          params = { exhibition: undefined, access: undefined, virtual: undefined }
      }
      onFilterChange(params)
    },
    [isLoggedIn, onFilterChange, showLogin]
  )

  const onOtherSelectFilterChange = useCallback(
    (_, data) => {
      const params = { [data.name]: data.value === 'all' ? undefined : data.value }
      onFilterChange(params)
    },
    [onFilterChange]
  )

  const setSingleDate = useCallback(() => {
    if (datePickerRange.from && !datePickerRange.to) {
      onFilterChange({ date: formatEventDate(datePickerRange.from), startDate: undefined, endDate: undefined })
    }
  }, [datePickerRange.from, datePickerRange.to, onFilterChange])

  const setDateRange = useCallback(
    (day) => {
      const { from } = datePickerRange

      if (from && day) {
        let params

        setDatePickerRange((prev) => ({ ...prev, to: day }))

        if (new Date(from).getTime() === new Date(day).getTime()) {
          params = { date: formatEventDate(from), startDate: undefined, endDate: undefined }
        } else {
          params = {
            date: undefined,
            startDate: `${formatEventDate(from)}T00:00:00`,
            endDate: `${formatEventDate(day)}T12:00:00`
          }
        }

        onFilterChange(params)
      } else {
        onFilterChange({ date: undefined, startDate: undefined, endDate: undefined })
      }
    },
    [datePickerRange, onFilterChange]
  )

  useEffect(() => {
    setDatePickerRange({
      from:
        (eventsFilters.date && new Date(eventsFilters.date)) ||
        (eventsFilters.startDate && new Date(eventsFilters.startDate)) ||
        undefined,
      to:
        (eventsFilters.date && new Date(eventsFilters.date)) ||
        (eventsFilters.endDate && new Date(eventsFilters.endDate)) ||
        undefined
    })
  }, [eventsFilters.date, eventsFilters.startDate, eventsFilters.endDate])

  return (
    <>
      <Container className={classNames(className, `${mID}__filters`)}>
        <div>
          <Row
            className={classNames(`${mID}__toggle mobile only`, {
              'toggle--on': isMobileFiltersVisible
            })}
          >
            <Col xs={12}>
              <div
                className={`${mID}__toggle-btn`}
                role='button'
                tabIndex={0}
                onClick={toggleMobileFilters}
                onKeyUp={toggleMobileFilters}
              >
                <Text forwardedAs='span' isSmall>
                  {eventsLabels.las_grp1_filters_for_mobile || 'Filters'}
                </Text>{' '}
                <Icon name='chevron-down' />
              </div>
            </Col>
          </Row>

          <Row
            className={classNames(
              `${mID}__filters`,
              { 'otm-browse-events__filters--off': !isMobileFiltersVisible },
              { 'otm-browse-events__filters--on': isMobileFiltersVisible }
            )}
          >
            {/* Location Filter */}
            {locationsFilterVisible &&
              availableFilters.includes('location') &&
              eventsFiltersData.LOCATION &&
              eventsFiltersData.LOCATION.length > 0 && (
                <Col md={12}>
                  <BrowseLocations
                    // @ts-ignore
                    title={locationLabel}
                    allLabel={allLabel}
                    eventsFilters={eventsFilters}
                    locationsData={eventsFiltersData.LOCATION}
                    moreLocationsLabel={moreLocationsLabel}
                    selectedNumberLabel={selectedNumberLabel}
                    onFilterChange={onFilterChange}
                  />
                </Col>
              )}
            {/* Type Filter */}
            {availableFilters.includes('type') && eventsFiltersData.ACCESS && eventsFiltersData.ACCESS.length > 0 && (
              <Col md={3} xs={12}>
                <div>
                  <Dropdown
                    fluid
                    label={typeLabel}
                    options={buildEventsFilter(
                      [...defaultTypeOptions, ...eventsFiltersData.ACCESS],
                      selectItem,
                      allLabel
                    )}
                    onChange={onTypeFilterChange}
                    value={typeFilterSelectedValue}
                    name='type'
                    selection
                  />
                </div>
              </Col>
            )}
            {/* Day Filter */}
            {availableFilters.includes('date') && (
              <Col md={3} xs={12}>
                <div className='DayPickerInput__field'>
                  <label className='fw-bold' htmlFor='day'>
                    {dayLabel}
                  </label>
                  <div className='DayPickerInput'>
                    <input
                      type='text'
                      value={getDateRangeDisplayInputValue(datePickerRange.from, datePickerRange.to, allLabel)}
                      readOnly
                      className='border-0'
                    />
                  </div>
                  <DayPickerInput
                    placeholder={'allLabel'}
                    value={datePickerRange.from}
                    onDayChange={(day) => setDatePickerRange({ from: day, to: undefined })}
                    dayPickerProps={{
                      selectedDays: [datePickerRange.from, { from: datePickerRange.from, to: datePickerRange.to }],
                      modifiers: {
                        eventDay,
                        start: datePickerRange.from,
                        end: datePickerRange.to
                      },
                      modifiersStyles,
                      disabledDays: isDayDisabled,
                      onDayClick: (_, modifier) => !modifier.disabled && toDateRef.current.getInput().focus()
                    }}
                    inputProps={{
                      readOnly: true,
                      name: 'from'
                    }}
                    format={calendarDateFormat}
                    formatDate={formatDateDayPicker}
                    parseDate={parseDateDayPicker}
                    style={{ backgroundColor: 'white' }}
                    hideOnDayClick
                  />

                  <DayPickerInput
                    ref={toDateRef}
                    placeholder={allLabel}
                    value={datePickerRange.to}
                    onDayChange={setDateRange}
                    dayPickerProps={{
                      selectedDays: [datePickerRange.from, { from: datePickerRange.from, to: datePickerRange.to }],
                      modifiers: {
                        eventDay,
                        start: datePickerRange.from,
                        end: datePickerRange.to
                      },
                      modifiersStyles,
                      disabledDays: [isDayDisabled, { before: datePickerRange.from }],
                      month: datePickerRange.from
                    }}
                    inputProps={{
                      readOnly: true,
                      name: 'to'
                    }}
                    format={calendarDateFormat}
                    formatDate={formatDateDayPicker}
                    parseDate={parseDateDayPicker}
                    onDayPickerHide={setSingleDate}
                    hideOnDayClick
                  />

                  {datePickerRange.from && datePickerRange.to && (
                    <button className='DayPickerInput__clear' type='button' onClick={() => setDateRange(undefined)}>
                      <Icon name='close' />
                    </button>
                  )}
                  <i className='DayPickerInput__icon' />
                </div>
              </Col>
            )}
            {/* Category Filter */}
            {availableFilters.includes('category') &&
              eventsFiltersData.EVENT_TYPE &&
              eventsFiltersData.EVENT_TYPE.length > 0 && (
                <Col md={3} xs={12}>
                  <div>
                    <Dropdown
                      fluid
                      label={categoryLabel}
                      options={buildEventsFilter(eventsFiltersData.EVENT_TYPE, selectItem, allLabel)}
                      onChange={onOtherSelectFilterChange}
                      value={eventsFilters.eventTypes || 'all'}
                      name='eventTypes'
                      selection
                    />
                  </div>
                </Col>
              )}
            {/* Sort By Filter */}
            {availableFilters.includes('sort') && Object.keys(eventsFiltersData).length > 0 && (
              <Col md={3} xs={12}>
                <div>
                  <label className='fw-bold'>{sortByLabel}</label>
                  <Dropdown
                    options={buildEventsFilter(defaultOrderByOptions, selectItem, allLabel, false)}
                    onChange={(value) => {
                      const params = { orderBy: value === 'all' ? undefined : value }
                      onFilterChange(params)
                    }}
                    value={eventsFilters.orderBy}
                    name='orderBy'
                  />
                </div>
              </Col>
            )}
          </Row>
        </div>
      </Container>
    </>
  )
}

OtmBrowseFilters.propTypes = {
  className: string,
  isLoggedIn: bool,
  eventsLabels: object,
  eventsFilters: object,
  eventsFiltersData: object,
  locationLabel: string,
  typeLabel: string,
  dayLabel: string,
  categoryLabel: string,
  sortByLabel: string,
  allLabel: string,
  allEventsLabel: string,
  allExhibitionsLabel: string,
  allVirtualEventsLabel: string,
  endDateLabel: string,
  startDateLabel: string,
  recentlyAddedLabel: string,
  moreLocationsLabel: string,
  selectedNumberLabel: string,
  availableFilters: array,
  onFilterChange: func,
  locationVisibility: string
}

export default styled(OtmBrowseFilters)`
  position: relative;
  z-index: 3;

  .DayPickerInput__field {
    height: 100%;

    .DayPickerInput {
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      border-bottom-color: ${tokens.color.light.base.neutrals['300'].value};

      &:first-of-type {
        pointer-events: none;
        z-index: 2;
        ${mq.lessThan('desktop')`position: static;`};
      }

      &:nth-of-type(2) {
        z-index: 1;
      }

      &:not(:first-of-type) {
        input {
          opacity: 0;
          border: none;
        }
      }

      &-Overlay {
        z-index: 2;
      }

      &__clear {
        position: absolute;
        right: ${px2rem(20)};
        bottom: ${px2rem(4)};
        z-index: 2;
        display: block;
        padding: ${px2rem(8)};
        line-height: 1;
        background: transparent;
        border: none;
        cursor: pointer;

        svg {
          width: ${px2rem(12)};
          height: ${px2rem(12)};
        }
      }

      &__icon {
        bottom: ${px2rem(11)};
        width: ${px2rem(16)};
        height: ${px2rem(16)};
        z-index: 2;
        pointer-events: none;
      }

      .DayPicker-Day--selected:not(.DayPicker-Day--outside) {
        &,
        &.DayPicker-Day--disabled {
          background: ${tokens.color.light.base.neutrals['500'].value};
        }

        &:not(.DayPicker-Day--disabled) {
          color: var(--bs-mch-black) !important;
        }

        &.DayPicker-Day--disabled {
          color: ${tokens.color.light.base.neutrals['700'].value} !important;
        }

        &.DayPicker-Day--start,
        &.DayPicker-Day--end {
          color: var(--bs-mch-white) !important;
          background: var(--bs-mch-gray-500);
        }
      }
    }
  }

  .${mID} {
    &__filters {
      margin-bottom: ${px2rem(64)};

      &.row > .col {
        ${mq.lessThan('desktop')`margin-bottom: ${px2rem(24)};`};
      }

      &--on.row {
        ${mq.lessThan('desktop')`margin-bottom: 0;padding-top: 0;`};
      }

      &--off.row {
        ${mq.lessThan('desktop')`display: none;`};
      }
    }

    &__toggle {
      cursor: pointer;

      &.toggle--on {
        svg {
          transform: rotate(180deg);
        }
      }

      &-btn {
        border-bottom: ${px2rem(1)} solid ${tokens.color.light.base.neutrals['300'].value};
        padding-bottom: ${px2rem(14)};
        display: flex;
        align-items: center;
        font-weight: ${tokens.fontWeights.artBaselHeadline0.value};

        svg {
          width: ${px2rem(24)};
          height: ${px2rem(24)};
          margin-left: auto;
        }
      }

      &.row {
        margin-bottom: ${px2rem(40)};
        padding-bottom: 0;
      }
    }
  }
`
